import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ErroApi } from '../../nucleo/api';
import { textoDoErro, textoDoItem } from '../../nucleo/erros';
import { pode, useSessao } from '../../nucleo/sessao';
import type { Config, Estado, Recurso, Servico } from './tipos';
import { BOTAO_PRIMARIO, COR_ESTADO } from './tipos';

export const useRecursos = () => useQuery({ queryKey: ['agenda', 'recursos'], queryFn: () => api<Recurso[]>('/api/v1/agenda/recursos') });
export const useServicos = () => useQuery({ queryKey: ['agenda', 'servicos'], queryFn: () => api<Servico[]>('/api/v1/agenda/servicos') });
export const useConfig = () => useQuery({ queryKey: ['agenda', 'config'], queryFn: () => api<Config>('/api/v1/agenda/config') });
export const useMembros = () => useQuery({ queryKey: ['agenda', 'membros'], queryFn: () => api<{ usuario_id: string; nome: string }[]>('/api/v1/agenda/membros'), staleTime: 60_000 });

/** Botão primário da aba: abre o formulário pela URL (?novo=1), só para quem pode criar. */
export function AcaoNovo({ rotulo }: { rotulo: string }) {
  const { t } = useTranslation('agenda');
  const eu = useSessao();
  const [params, setParams] = useSearchParams();
  if (!pode(eu, 'agenda.criar')) return null;
  return (
    <button type="button" onClick={() => setParams({ ...Object.fromEntries(params), novo: '1' })} className={BOTAO_PRIMARIO}>
      {t(rotulo)}
    </button>
  );
}

export function useFecharNovo() {
  const [params, setParams] = useSearchParams();
  return {
    aberto: params.get('novo') === '1',
    fechar: () => {
      params.delete('novo');
      setParams(params, { replace: true });
    },
  };
}

export function SeloEstado({ estado }: { estado: Estado }) {
  const { t } = useTranslation('agenda');
  return <span className={`inline-flex shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${COR_ESTADO[estado]}`}>{t(`estado.${estado}`)}</span>;
}

export function Bloco({ titulo, acao, children }: { titulo: string; acao?: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0 rounded-xl border border-borda bg-sup-1 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="kicker">{titulo}</h2>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** Formatação de dinheiro e data no idioma da sessão. */
export function useFormatos() {
  const { i18n } = useTranslation();
  const l = i18n.language;
  return {
    dinheiro: (centavos: string | number | null, moeda = 'BRL') => (centavos === null ? '' : new Intl.NumberFormat(l, { style: 'currency', currency: moeda }).format(Number(centavos) / 100)),
    dataHora: (iso: string) => new Intl.DateTimeFormat(l, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)),
    hora: (iso: string) => new Intl.DateTimeFormat(l, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso)),
    dataLonga: (iso: string) => new Intl.DateTimeFormat(l, { weekday: 'long', day: '2-digit', month: 'long' }).format(new Date(iso)),
  };
}

/** Data (AAAA-MM-DD) e hora (HH:MM) locais do navegador para inputs. */
export const paraInputData = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const paraInputHora = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
export const deInputs = (data: string, hora: string) => new Date(`${data}T${hora}:00`);

/**
 * SB-01 · Erros da API distribuídos pelos campos do formulário, no idioma da tela. Mesma regra do errosPorCampo do
 * núcleo, mas traduzindo também pelos códigos próprios da Agenda (agenda:erros.<code>); o do núcleo só olha o
 * dicionário comum e mostraria a frase genérica com o código cru para fora_do_horario, periodo_invalido etc.
 */
export function errosDoForm(e: unknown, mapa: Record<string, string>): { campos: Record<string, string>; geral: string | null } {
  if (!e) return { campos: {}, geral: null };
  if (!(e instanceof ErroApi)) return { campos: {}, geral: textoDoErro(e, 'agenda') };
  const todos = e.erros.length ? e.erros : [{ code: e.code, message: e.message, details: e.details }];
  const campos: Record<string, string> = {};
  const restantes: string[] = [];
  for (const x of todos) {
    // SPEC-BASE-R29-01: erro de campo personalizado (campo_obrigatorio, campo_invalido) vai para extras.<chave>, como no núcleo.
    const campoExtra = (x.details as { campo?: string } | undefined)?.campo;
    const campo = campoExtra && x.code.startsWith('campo_') ? `extras.${campoExtra}` : mapa[x.code];
    const texto = textoDoItem(x, 'agenda');
    if (campo && !campos[campo]) campos[campo] = texto;
    else restantes.push(texto);
  }
  return { campos, geral: restantes.length ? restantes.join(' ') : null };
}
